import { useQuery } from "@tanstack/react-query";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import type { AddressDto, OfficeRndProjectConfigurationDto, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Accordion } from "@/design-system/Accordion";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type OfficeRndFormValues = {
  enabled: boolean;
  baseUrl: string;
  clientId: string;
  clientSecret: string;
  requesterUser: UserDto | undefined;
  initialImportStartDate: Date | undefined;
  fallbackAddress: AddressDto | undefined;
  addressMappings: Array<{
    prefix: string;
    address: AddressDto | undefined;
  }>;
  dryRunEnabled: boolean;
};

export function OfficeRndPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();
  const addressQueries = useAddressQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.officeRndConfiguration());
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery(userQueries.listAdmins());
  const {
    data: addresses,
    isFetching: isLoadingAddresses,
    error: addressesError,
  } = useQuery(addressQueries.list({ limit: 1000 }));
  const mutation = integrationMutations.useEditOfficeRndConfiguration();

  if (
    !hasPermission((x) => x.integrations.canManageOfficeRnd) ||
    !hasPermission((x) => x.addressManagement.canListAddresses)
  ) {
    return <ErrorPage status={404} />;
  }

  if (configError || adminUsersError || addressesError) {
    return <ErrorPage error={configError || adminUsersError || addressesError} />;
  }

  if (isLoadingConfig || isLoadingAdminUsers || isLoadingAddresses) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.office-rnd.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <OfficeRndForm
        defaultValues={{
          enabled: config?.enabled || false,
          baseUrl: config?.baseUrl || "",
          clientId: config?.clientId || "",
          clientSecret: config?.clientSecret || "",
          requesterUser:
            !config?.requesterUserId || !adminUsers
              ? undefined
              : adminUsers.find((u) => u.id === config.requesterUserId),
          initialImportStartDate: config?.initialImportStartDate ? new Date(config.initialImportStartDate) : undefined,
          fallbackAddress:
            !config?.fallbackAddressId || !addresses
              ? undefined
              : addresses.items.find((a) => a.id === config.fallbackAddressId),
          addressMappings:
            config?.addressMappings.map((m) => ({
              prefix: m.prefix,
              address: addresses?.items.find((a) => a.id === m.addressId),
            })) || [],
          dryRunEnabled: config?.dryRunEnabled || false,
        }}
        adminUsers={adminUsers || []}
        addresses={addresses?.items || []}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

type OfficeRndFormProps = {
  defaultValues: Partial<OfficeRndFormValues>;
  adminUsers: UserDto[];
  addresses: AddressDto[];
  isSubmitting: boolean;
  onSubmit: (data: OfficeRndProjectConfigurationDto) => void;
};

function OfficeRndForm({
  adminUsers,
  addresses,
  defaultValues,
  isSubmitting,
  onSubmit,
}: OfficeRndFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<OfficeRndFormValues>({ defaultValues });

  const currEnabled = useWatch({ name: "enabled", control: formMethods.control });

  const { fields, append, remove } = useFieldArray({ control: formMethods.control, name: "addressMappings" });

  const handleSubmit = (formValues: OfficeRndFormValues) => {
    onSubmit({
      enabled: formValues.enabled,
      baseUrl: formValues.baseUrl,
      clientId: formValues.clientId,
      clientSecret: formValues.clientSecret,
      requesterUserId: formValues.requesterUser?.id,
      initialImportStartDate: formValues.initialImportStartDate?.toISOString(),
      fallbackAddressId: formValues.fallbackAddress?.id,
      addressMappings: formValues.addressMappings.map((m) => ({ prefix: m.prefix, addressId: m.address?.id ?? "" })),
      dryRunEnabled: formValues.dryRunEnabled,
    });
  };

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.office-rnd.title")}>
        <Form.Body>
          <FormCheckbox<OfficeRndFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              if (currEnabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField label={t("page.integration.office-rnd.form.base-url")} required>
            <FormInput<OfficeRndFormValues, "baseUrl">
              id="baseUrl"
              name="baseUrl"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.enabled) {
                      return createRequiredStringRule(t, "page.integration.office-rnd.form.base-url")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              disabled={isSubmitting || !currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.office-rnd.form.client-id")} required>
            <FormInput<OfficeRndFormValues, "clientId">
              id="clientId"
              name="clientId"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.enabled) {
                      return createRequiredStringRule(t, "page.integration.office-rnd.form.client-id")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              disabled={isSubmitting || !currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.office-rnd.form.client-secret")} required>
            <FormInput<OfficeRndFormValues, "clientSecret">
              id="clientSecret"
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.enabled) {
                      return createRequiredStringRule(t, "page.integration.office-rnd.form.api-key")(value, formValues);
                    }
                  },
                },
              }}
              disabled={isSubmitting || !currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.office-rnd.form.requester-user")} required>
            <div className="max-w-search-wide">
              <FormSelect<OfficeRndFormValues, UserDto>
                items={adminUsers || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                name="requesterUser"
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.office-rnd.form.requester-user"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>

          <FormField label={t("page.integration.office-rnd.form.initial-import-start-date")} required>
            <div className="max-w-search-wide">
              <FormDatePicker<OfficeRndFormValues, "initialImportStartDate">
                name="initialImportStartDate"
                type="datetime"
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.office-rnd.form.initial-import-start-date"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>

          <FormField label={t("page.integration.office-rnd.form.fallback-address")} required>
            <div className="max-w-search-wide">
              <FormSelect<OfficeRndFormValues, AddressDto>
                items={addresses || []}
                keySelector={(address) => address.id}
                renderOption={(address) => address.location || address.id}
                name="fallbackAddress"
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.office-rnd.form.fallback-address"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>

          <Accordion.Root>
            <Accordion.Item value="address-mappings">
              <Accordion.Trigger>{t("page.integration.office-rnd.form.address-mappings")}</Accordion.Trigger>
              <Accordion.Content>
                <Stack gap="sm">
                  {fields.map((field, index) => (
                    <Inline key={field.id} gap="sm" align="center">
                      <div className="flex-1">
                        <FormInput<OfficeRndFormValues>
                          name={`addressMappings.${index}.prefix`}
                          placeholder={t("page.integration.office-rnd.form.address-mappings.prefix")}
                          disabled={isSubmitting || !currEnabled}
                        />
                      </div>
                      <div className="flex-1">
                        <FormSelect<OfficeRndFormValues, AddressDto>
                          items={addresses || []}
                          keySelector={(address) => address.id}
                          renderOption={(address) => address.location || address.id}
                          name={`addressMappings.${index}.address`}
                          placeholder={t("page.integration.office-rnd.form.address-mappings.address")}
                          disabled={isSubmitting || !currEnabled}
                        />
                      </div>
                      <IconButton
                        type="button"
                        variant="filled"
                        tone="error"
                        title={t("common.action.delete")}
                        icon={<Trash01Icon />}
                        onClick={() => remove(index)}
                        disabled={isSubmitting || !currEnabled}
                      />
                    </Inline>
                  ))}
                  <Button
                    type="button"
                    variant="soft"
                    icon={<PlusIcon />}
                    onClick={() => append({ prefix: "", address: undefined })}
                    disabled={isSubmitting || !currEnabled}
                  >
                    {t("common.action.add")}
                  </Button>
                </Stack>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion.Root>
        </Form.Body>

        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
